import React, { useEffect, useState } from 'react';
import { API_BASE } from '../config';

/**
 * User-facing dashboard. Visible to any signed-in user.
 * Shows:
 *  - Profile + role badge
 *  - API key (copy + regenerate)
 *  - Usage meters (AI + API)
 *  - AI history
 *  - Billing — current plan + Upgrade to Pro (Stripe Checkout)
 */
function UserDashboard({ onBack, onSignOut, onStartCheckout }) {
  const [me, setMe] = useState(null);
  const [aiQueries, setAiQueries] = useState([]);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [upgradeMsg, setUpgradeMsg] = useState('');
  const [activeTab, setActiveTab] = useState('overview'); // overview | ai | billing

  const token = typeof window !== 'undefined' ? localStorage.getItem('astrogem_token') : null;

  useEffect(() => {
    if (!token) {
      setError('Not signed in.');
      setLoading(false);
      return;
    }
    Promise.all([
      fetch(`${API_BASE}/api/v3/user/me`, { headers: { Authorization: 'Bearer ' + token } }).then(r => r.json()),
      fetch(`${API_BASE}/api/v3/user/ai-queries?limit=20`, { headers: { Authorization: 'Bearer ' + token } }).then(r => r.json()),
      fetch(`${API_BASE}/api/v3/user/billing`, { headers: { Authorization: 'Bearer ' + token } }).then(r => r.json()),
    ])
      .then(([meRes, aiRes, billRes]) => {
        if (!meRes.success) throw new Error(meRes.error || 'Failed to load profile');
        setMe(meRes);
        setAiQueries(aiRes.queries || []);
        setBilling(billRes);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const copyApiKey = () => {
    if (!me?.api_key) return;
    navigator.clipboard.writeText(me.api_key);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const regenerateKey = async () => {
    if (!window.confirm('Regenerate API key? Your old key will stop working immediately.')) return;
    const r = await fetch(`${API_BASE}/api/v3/user/api-key/regenerate`, {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + token },
    }).then(r => r.json());
    if (r.success) {
      setMe({ ...me, api_key: r.api_key });
      localStorage.setItem('astrogem_api_key', r.api_key);
    } else {
      alert('Failed: ' + (r.error || 'Unknown error'));
    }
  };

  // Both checkout actions now navigate to our in-app embedded Payment Element page
  // (instead of redirecting to Stripe Checkout). Source of truth: webhook updates DB.
  const startSubscriptionCheckout = (tier, period = 'monthly') => {
    setUpgradeMsg('');
    if (onStartCheckout) onStartCheckout({ kind: 'subscription', tier, period });
  };

  const buyCreditPack = (pack) => {
    setUpgradeMsg('');
    if (onStartCheckout) onStartCheckout({ kind: 'credit_pack', pack });
  };

  if (loading) return <Centered>Loading dashboard…</Centered>;
  if (error) return <Centered>⚠️ {error}</Centered>;
  if (!me) return <Centered>No profile data.</Centered>;

  const u = me.user;
  const ai = me.usage.ai_queries;
  const api = me.usage.api_calls;
  const creditsBalance = me.credits_balance || 0;

  const RoleBadge = ({ role }) => {
    const colors = {
      free: 'bg-slate-700 text-slate-200',
      pro: 'bg-purple-600 text-white',
      enterprise: 'bg-amber-600 text-white',
      comp: 'bg-green-600 text-white',
      admin: 'bg-red-600 text-white',
    };
    return <span className={`text-xs font-bold uppercase tracking-wide px-2 py-1 rounded ${colors[role] || colors.free}`}>{role}</span>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 text-white">
      <nav className="border-b border-slate-800 bg-slate-900/50 backdrop-blur sticky top-0 z-50">
        <div className="px-4 py-3 max-w-6xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <button onClick={onBack} className="text-white/60 hover:text-white text-sm">← Back to app</button>
          <div className="flex items-center gap-3">
            <span className="text-sm text-white/70">{u.email}</span>
            <RoleBadge role={u.role} />
            {u.role === 'admin' && (
              <a href="#/admin" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('astrogem-nav', { detail: 'admin' })); }}
                 className="text-xs px-2 py-1 bg-red-600/20 border border-red-500/40 rounded hover:bg-red-600/30">
                Admin Panel →
              </a>
            )}
            <button onClick={onSignOut} className="text-xs text-white/50 hover:text-white">Sign out</button>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-1">Hi {u.name || u.email.split('@')[0]} 👋</h1>
        <p className="text-white/60 mb-6">Your AstroGem account</p>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-800">
          {[
            ['overview', '📊 Overview'],
            ['ai', '🪔 AI History'],
            ['billing', '💳 Billing'],
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`px-4 py-2 text-sm transition ${activeTab === id ? 'border-b-2 border-purple-400 text-white' : 'text-white/50 hover:text-white'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <AiUsageCard
                used={ai.used}
                limit={ai.limit}
                creditsBalance={creditsBalance}
                onBuyCredits={() => setActiveTab('billing')}
              />
              <UsageCard
                title="API Calls"
                used={api.used}
                limit={api.limit}
                period="this month"
                hint="All /api/v3/* endpoints"
              />
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold">🔑 Your API Key</h3>
                <RoleBadge role={u.role} />
              </div>
              <p className="text-white/60 text-sm mb-3">Use this in the <code className="bg-slate-900 px-1.5 py-0.5 rounded">x-api-key</code> header for programmatic access.</p>
              <div className="flex gap-2">
                <code className="flex-1 bg-slate-900 border border-slate-700 rounded px-3 py-2 font-mono text-xs text-green-300 break-all">
                  {me.api_key}
                </code>
                <button onClick={copyApiKey} className="px-3 py-2 bg-purple-600/30 hover:bg-purple-600/40 border border-purple-500/40 rounded text-sm whitespace-nowrap">
                  {copied ? '✓ Copied' : '📋 Copy'}
                </button>
                <button onClick={regenerateKey} className="px-3 py-2 bg-red-600/20 hover:bg-red-600/30 border border-red-500/40 rounded text-sm whitespace-nowrap">
                  🔄 Regenerate
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AI HISTORY TAB */}
        {activeTab === 'ai' && (
          <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
            <h3 className="text-lg font-bold mb-4">Your AI Conversations</h3>
            {aiQueries.length === 0 ? (
              <p className="text-white/50 text-sm">No AI questions yet. Generate a chart and ask the AI-Strologer.</p>
            ) : (
              <div className="space-y-3">
                {aiQueries.map(q => (
                  <div key={q.id} className="bg-slate-900 border border-slate-700 rounded p-4">
                    <div className="flex items-center justify-between mb-1 text-xs text-white/40">
                      <span>{new Date(q.created_at).toLocaleString()}</span>
                      {q.cost_usd != null && <span>${q.cost_usd?.toFixed(5)} · {q.total_tokens} tok · {q.duration_ms}ms</span>}
                    </div>
                    <p className="text-purple-300 text-sm font-semibold mb-1">Q: {q.question_preview}</p>
                    <p className="text-white/80 text-sm whitespace-pre-line">{q.response_preview}{q.response_preview && q.response_preview.length === 300 ? '…' : ''}</p>
                    {q.error && <p className="text-red-300 text-xs mt-1">⚠ {q.error}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* BILLING TAB */}
        {activeTab === 'billing' && (
          <div className="space-y-6">
            {/* Current plan */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
              <h3 className="text-lg font-bold mb-3">Current Plan</h3>
              <div className="flex items-center gap-3 mb-4">
                <RoleBadge role={u.role} />
                {billing?.subscription?.status && <span className="text-sm text-white/60">{billing.subscription.status}</span>}
                {creditsBalance > 0 && (
                  <span className="text-sm text-amber-300">🪙 {creditsBalance.toLocaleString()} credit{creditsBalance === 1 ? '' : 's'}</span>
                )}
              </div>
              <p className="text-white/70 text-sm">
                {u.role === 'free' && 'Free plan: 3 AI questions + 100 API calls/month.'}
                {u.role === 'pro' && 'Pro plan: 200 AI questions + 10,000 API calls/month.'}
                {u.role === 'developer' && 'Developer plan: 1,500 AI questions + 50,000 API calls/month.'}
                {u.role === 'scale' && 'Scale plan: 10,000 AI questions + 500,000 API calls/month.'}
                {u.role === 'enterprise' && 'Enterprise plan — unlimited usage.'}
                {u.role === 'comp' && 'Complimentary account — enjoy 🪔'}
                {u.role === 'admin' && 'Admin account — unlimited usage + admin panel access.'}
              </p>
              {upgradeMsg && <p className="mt-3 text-amber-300 text-sm">{upgradeMsg}</p>}
            </div>

            {/* Subscription tiers — only show those the user can upgrade to */}
            {['free', 'pro', 'developer'].includes(u.role) && (
              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
                <h3 className="text-lg font-bold mb-1">{u.role === 'free' ? 'Upgrade your plan' : 'Change plan'}</h3>
                <p className="text-white/40 text-xs mb-4">Yearly saves ~17%. Cancel anytime.</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[
                    { tier: 'pro',       label: 'Pro',       monthly: 19,  yearly: 190,   ai: 200,    api: '10K',   show: u.role === 'free' },
                    { tier: 'developer', label: 'Developer', monthly: 49,  yearly: 490,   ai: 1500,   api: '50K',   show: ['free', 'pro'].includes(u.role) },
                    { tier: 'scale',     label: 'Scale',     monthly: 199, yearly: 1990,  ai: 10000,  api: '500K',  show: ['free', 'pro', 'developer'].includes(u.role) },
                  ].filter(p => p.show).map(plan => (
                    <div key={plan.tier} className="bg-slate-900 border border-slate-700 rounded-lg p-4">
                      <h4 className="font-bold mb-1">{plan.label}</h4>
                      <p className="text-white/50 text-xs mb-3">{plan.ai.toLocaleString()} AI / {plan.api} API per month</p>
                      <button onClick={() => startSubscriptionCheckout(plan.tier, 'monthly')}
                        className="w-full mb-2 px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded text-sm font-bold transition">
                        ${plan.monthly}/mo
                      </button>
                      <button onClick={() => startSubscriptionCheckout(plan.tier, 'yearly')}
                        className="w-full px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded text-sm transition">
                        ${plan.yearly}/yr (save 17%)
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* AI Credit packs */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
              <h3 className="text-lg font-bold mb-1">🪙 Buy AI Credits</h3>
              <p className="text-white/60 text-sm mb-1">
                Used after your monthly AI allowance runs out. <strong>Never expire.</strong>
              </p>
              <p className="text-white/40 text-xs mb-4">
                Current balance: <span className="text-amber-300 font-bold">{creditsBalance.toLocaleString()}</span> credit{creditsBalance === 1 ? '' : 's'}
              </p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { pack: 'pack_500',  credits: 500,   price: 9,   per: '$0.018' },
                  { pack: 'pack_2k',   credits: 2000,  price: 29,  per: '$0.0145' },
                  { pack: 'pack_10k',  credits: 10000, price: 99,  per: '$0.0099', best: true },
                  { pack: 'pack_50k',  credits: 50000, price: 399, per: '$0.008' },
                ].map(p => (
                  <div key={p.pack} className={`rounded-lg p-4 text-center ${p.best ? 'bg-gradient-to-br from-amber-500/20 to-pink-500/10 border-2 border-amber-500/50' : 'bg-slate-900 border border-slate-700'}`}>
                    {p.best && <div className="text-[10px] font-bold text-amber-300 mb-1">BEST VALUE</div>}
                    <div className="text-xl font-bold">{p.credits.toLocaleString()}</div>
                    <p className="text-white/50 text-xs">credits</p>
                    <div className="text-lg font-bold mt-2">${p.price}</div>
                    <p className="text-white/40 text-[10px] mb-2">{p.per}/credit</p>
                    <button onClick={() => buyCreditPack(p.pack)}
                      className="w-full px-2 py-1.5 bg-purple-600/30 hover:bg-purple-600/50 border border-purple-500/40 rounded text-xs font-bold transition">
                      Buy
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
              <h3 className="text-lg font-bold mb-3">Payment History</h3>
              {!billing?.payments?.length ? (
                <p className="text-white/50 text-sm">No payments yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-left text-white/40 text-xs uppercase tracking-wide border-b border-slate-700">
                    <tr><th className="py-2">Date</th><th>Type</th><th>Description</th><th className="text-right">Amount</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {billing.payments.map(p => (
                      <tr key={p.id} className="border-b border-slate-800">
                        <td className="py-2">{new Date(p.created_at).toLocaleDateString()}</td>
                        <td>{p.type}</td>
                        <td className="text-white/70">{p.description || '—'}</td>
                        <td className="text-right">${(p.amount_cents / 100).toFixed(2)} {p.currency.toUpperCase()}</td>
                        <td><span className={`text-xs px-2 py-0.5 rounded ${p.status === 'succeeded' || p.status === 'paid' ? 'bg-green-700' : 'bg-slate-700'}`}>{p.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function UsageCard({ title, used, limit, period, hint }) {
  const isUnlimited = limit === -1;
  const pct = isUnlimited ? 0 : Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
  const dangerous = pct > 80;
  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-bold">{title}</h3>
        <span className="text-xs text-white/40">{period}</span>
      </div>
      <p className="text-white/40 text-xs mb-3">{hint}</p>
      <div className="text-3xl font-bold mb-2">
        {used}
        <span className="text-white/40 text-base ml-1">
          {isUnlimited ? '/ ∞' : `/ ${limit}`}
        </span>
      </div>
      {!isUnlimited && (
        <div className="w-full h-2 bg-slate-900 rounded-full overflow-hidden">
          <div
            className={`h-full ${dangerous ? 'bg-red-500' : 'bg-gradient-to-r from-purple-500 to-pink-500'}`}
            style={{ width: pct + '%' }}
          />
        </div>
      )}
    </div>
  );
}

// AI usage card — special because it has two pools (plan + credits balance)
function AiUsageCard({ used, limit, creditsBalance, onBuyCredits }) {
  const isUnlimited = limit === -1;
  const planRemaining = isUnlimited ? -1 : Math.max(0, limit - used);
  const planExhausted = !isUnlimited && planRemaining === 0;
  const pct = isUnlimited ? 0 : Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
  const dangerous = pct > 80;
  const totalAvailable = isUnlimited ? -1 : planRemaining + creditsBalance;

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-bold">🪔 AI Questions</h3>
        <span className="text-xs text-white/40">this month</span>
      </div>
      <p className="text-white/40 text-xs mb-3">DeepSeek-powered Vedic chart Q&A</p>

      <div className="text-3xl font-bold mb-1">
        {isUnlimited ? '∞ available' : `${totalAvailable} available`}
      </div>
      {!isUnlimited && (
        <p className="text-white/50 text-xs mb-3">
          Plan: <strong>{planRemaining}/{limit}</strong> left
          {creditsBalance > 0 && <> · 🪙 Credits: <strong className="text-amber-300">{creditsBalance.toLocaleString()}</strong></>}
        </p>
      )}

      {!isUnlimited && (
        <div className="w-full h-2 bg-slate-900 rounded-full overflow-hidden mb-3">
          <div
            className={`h-full transition-all ${dangerous ? 'bg-red-500' : 'bg-gradient-to-r from-purple-500 to-pink-500'}`}
            style={{ width: pct + '%' }}
          />
        </div>
      )}

      {planExhausted && creditsBalance === 0 && (
        <div className="bg-red-500/10 border border-red-500/40 rounded p-2 text-xs mb-3">
          ⚠️ Plan exhausted. Buy credits or upgrade to continue.
        </div>
      )}

      {!isUnlimited && (dangerous || planExhausted) && onBuyCredits && (
        <button onClick={onBuyCredits}
          className="w-full px-3 py-2 bg-amber-600/30 hover:bg-amber-600/40 border border-amber-500/40 rounded text-sm font-bold transition">
          🪙 Buy AI Credits
        </button>
      )}
    </div>
  );
}

function Centered({ children }) {
  return (
    <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center">
      <div className="text-white/60">{children}</div>
    </div>
  );
}

export default UserDashboard;
